import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const emptyManualLine = { item_id: '', qty: '', unit_cost: '', tax_percent: '0' };

function FoodPurchaseOrder({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [approvedPrs, setApprovedPrs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [poRows, setPoRows] = useState([]);
  const [pricingMap, setPricingMap] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [convertForm, setConvertForm] = useState({
    requisition_id: '',
    supplier_id: '',
    tax_percent: '0',
    delivery_date: ''
  });
  const [manualForm, setManualForm] = useState({
    supplier_id: '',
    delivery_date: '',
    lines: [{ ...emptyManualLine }]
  });

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    ...extra
  });

  const parseResponse = async (res) => {
    const raw = await res.text();
    try {
      const json = raw ? JSON.parse(raw) : {};
      return { data: json, message: json?.error || '' };
    } catch {
      return { data: {}, message: raw || '' };
    }
  };

  const loadData = async () => {
    if (!chainId || !propertyId) return;
    try {
      const [prRes, supplierRes, itemRes, poRes] = await Promise.all([
        fetch('/api/v1/food/pr?status=APPROVED', { headers: headers() }),
        fetch('/api/v1/food/masters/suppliers', { headers: headers() }),
        fetch('/api/v1/food/masters/items', { headers: headers() }),
        fetch('/api/v1/food/po', { headers: headers() })
      ]);
      const pr = await parseResponse(prRes);
      const supplier = await parseResponse(supplierRes);
      const item = await parseResponse(itemRes);
      const po = await parseResponse(poRes);

      if (!prRes.ok) return setError(pr.message || 'Failed to load approved requisitions');
      if (!supplierRes.ok) return setError(supplier.message || 'Failed to load suppliers');
      if (!itemRes.ok) return setError(item.message || 'Failed to load items');
      if (!poRes.ok) return setError(po.message || 'Failed to load purchase orders');

      const prs = Array.isArray(pr.data) ? pr.data : [];
      setApprovedPrs(prs);
      setSuppliers(Array.isArray(supplier.data) ? supplier.data : []);
      setItems(Array.isArray(item.data) ? item.data : []);
      setPoRows(Array.isArray(po.data) ? po.data : []);
      if (!convertForm.requisition_id && prs.length) setConvertForm((prev) => ({ ...prev, requisition_id: prs[0].id }));
    } catch {
      setError('Network error while loading data');
    }
  };

  useEffect(() => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      return;
    }
    setError('');
    setMessage('');
    loadData();
  }, [token, chainId, propertyId]);

  const loadPricingComparison = async (supplierId, itemIds) => {
    const filtered = (itemIds || []).filter(Boolean);
    if (!filtered.length) return setPricingMap({});
    try {
      const q = new URLSearchParams();
      q.set('item_ids', filtered.join(','));
      if (supplierId) q.set('supplier_id', supplierId);
      const res = await fetch(`/api/v1/food/po/item-pricing?${q.toString()}`, { headers: headers() });
      const { data } = await parseResponse(res);
      if (!res.ok) return;
      const next = {};
      (Array.isArray(data) ? data : []).forEach((row) => { next[String(row.item_id)] = row; });
      setPricingMap(next);
    } catch {
      setPricingMap({});
    }
  };

  useEffect(() => {
    const ids = manualForm.lines.map((l) => l.item_id).filter(Boolean);
    loadPricingComparison(manualForm.supplier_id, ids);
  }, [manualForm.supplier_id, JSON.stringify(manualForm.lines.map((l) => l.item_id))]);

  const convert = async () => {
    setError('');
    setMessage('');
    if (!convertForm.requisition_id) return setError('Select approved requisition');
    if (!convertForm.supplier_id) return setError('Select supplier');
    const tax = Number(convertForm.tax_percent || 0);
    if (Number.isNaN(tax) || tax < 0) return setError('Tax % must be 0 or more');
    try {
      const res = await fetch(`/api/v1/food/po/from-pr/${convertForm.requisition_id}`, {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          supplier_id: convertForm.supplier_id,
          tax_percent: tax,
          delivery_date: convertForm.delivery_date || null
        })
      });
      const { data, message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Conversion failed');
      setMessage(`PO #${data.po_no} created. Total: ${Number(data.total_amount || 0).toFixed(2)}`);
      setConvertForm((prev) => ({ ...prev, requisition_id: '', supplier_id: '', tax_percent: '0', delivery_date: '' }));
      loadData();
    } catch {
      setError('Network error while converting PR to PO');
    }
  };

  const addManualLine = () => setManualForm((prev) => ({ ...prev, lines: [...prev.lines, { ...emptyManualLine }] }));
  const removeManualLine = (idx) => setManualForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));
  const setManualLine = (idx, patch) => {
    setManualForm((prev) => ({ ...prev, lines: prev.lines.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)) }));
  };

  const createManualPo = async () => {
    setError('');
    setMessage('');
    if (!manualForm.supplier_id) return setError('Select supplier for manual PO');
    const lines = manualForm.lines
      .filter((l) => String(l.item_id || '').trim() && Number(l.qty || 0) > 0)
      .map((l) => ({
        item_id: l.item_id,
        qty: Number(l.qty || 0),
        unit_cost: Number(l.unit_cost || 0),
        tax_percent: Number(l.tax_percent || 0)
      }));
    if (!lines.length) return setError('Add at least one valid manual line');
    try {
      const res = await fetch('/api/v1/food/po/manual', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          supplier_id: manualForm.supplier_id,
          delivery_date: manualForm.delivery_date || null,
          lines
        })
      });
      const { data, message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Manual PO creation failed');
      setMessage(`Manual PO #${data.po_no} created. Total: ${Number(data.total_amount || 0).toFixed(2)}`);
      setManualForm({ supplier_id: '', delivery_date: '', lines: [{ ...emptyManualLine }] });
      setPricingMap({});
      loadData();
    } catch {
      setError('Network error while creating manual PO');
    }
  };

  const inputStyle = { padding: '0.55rem', border: '1px solid #dcdde1', borderRadius: '6px' };
  const btnBase = { padding: '0.5rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' };

  return (
    <div className="page-shell">
      <h2 style={{ color: '#2f855a' }}>Food Management - Purchase Order</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="filter-bar">
        <h3>Convert PR to PO</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem' }}>
          <select value={convertForm.requisition_id} onChange={(e) => setConvertForm({ ...convertForm, requisition_id: e.target.value })} style={inputStyle}>
            <option value="">Select Approved Requisition</option>
            {approvedPrs.map((r) => <option key={r.id} value={r.id}>{`PR #${r.requisition_no} - ${r.store_name || ''}`}</option>)}
          </select>
          <select value={convertForm.supplier_id} onChange={(e) => setConvertForm({ ...convertForm, supplier_id: e.target.value })} style={inputStyle}>
            <option value="">Select Supplier</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{`${s.supplier_id} - ${s.company_name}`}</option>)}
          </select>
          <input type="number" min="0" step="0.001" value={convertForm.tax_percent} onChange={(e) => setConvertForm({ ...convertForm, tax_percent: e.target.value })} style={inputStyle} placeholder="Tax %" />
          <input type="date" value={convertForm.delivery_date} onChange={(e) => setConvertForm({ ...convertForm, delivery_date: e.target.value })} style={inputStyle} />
        </div>
        <div style={{ marginTop: '0.7rem' }}>
          <button onClick={convert} className="btn-success" style={btnBase}>Convert to PO</button>
        </div>
      </div>

      <div className="filter-bar">
        <h3>Manual PO Creation (Without PR)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem', marginBottom: '0.7rem' }}>
          <select value={manualForm.supplier_id} onChange={(e) => setManualForm({ ...manualForm, supplier_id: e.target.value })} style={inputStyle}>
            <option value="">Select Supplier</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{`${s.supplier_id} - ${s.company_name}`}</option>)}
          </select>
          <input type="date" value={manualForm.delivery_date} onChange={(e) => setManualForm({ ...manualForm, delivery_date: e.target.value })} style={inputStyle} />
        </div>
        {manualForm.lines.map((ln, idx) => {
          const cmp = pricingMap[String(ln.item_id)] || null;
          return (
            <div key={idx} style={{ marginBottom: '0.6rem', padding: '0.6rem', background: '#fff', border: '1px solid #dcdde1', borderRadius: '6px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '2fr 1fr 1fr 1fr auto', gap: '0.45rem' }}>
                <select value={ln.item_id} onChange={(e) => setManualLine(idx, { item_id: e.target.value })} style={inputStyle}>
                  <option value="">Select Item</option>
                  {items.map((i) => <option key={i.id} value={i.id}>{`${i.item_code} - ${i.item_name}`}</option>)}
                </select>
                <input type="number" min="0.001" step="0.001" value={ln.qty} onChange={(e) => setManualLine(idx, { qty: e.target.value })} style={inputStyle} placeholder="Qty" />
                <input type="number" min="0" step="0.0001" value={ln.unit_cost} onChange={(e) => setManualLine(idx, { unit_cost: e.target.value })} style={inputStyle} placeholder="Unit Cost" />
                <input type="number" min="0" step="0.001" value={ln.tax_percent} onChange={(e) => setManualLine(idx, { tax_percent: e.target.value })} style={inputStyle} placeholder="Tax %" />
                <button onClick={() => removeManualLine(idx)} className="btn-danger" style={btnBase}>Remove</button>
              </div>
              {cmp && (
                <div style={{ marginTop: '0.5rem', color: '#4a5568', fontSize: '0.86rem' }}>
                  Std: {Number(cmp.standard_cost || 0).toFixed(4)} | Last: {Number(cmp.last_po_cost || 0).toFixed(4)} | Supplier Last: {Number(cmp.supplier_last_cost || 0).toFixed(4)} | Min: {Number(cmp.min_recent_cost || 0).toFixed(4)} | Max: {Number(cmp.max_recent_cost || 0).toFixed(4)} | Avg: {Number(cmp.avg_recent_cost || 0).toFixed(4)}
                </div>
              )}
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={addManualLine} className="btn-primary" style={btnBase}>Add Line</button>
          <button onClick={createManualPo} className="btn-success" style={btnBase}>Create Manual PO</button>
        </div>
      </div>

      <div className="filter-bar">
        <h3>Purchase Orders</h3>
        {poRows.length === 0 ? (
          <p>No purchase orders found</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#2f855a', color: '#fff' }}>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>PO No</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>PR No</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Supplier</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Quantity</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>UOM</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Subtotal</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Tax</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {poRows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.6rem' }}>{r.po_no}</td>
                  <td style={{ padding: '0.6rem' }}>{r.requisition_no || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.supplier_name || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.status}</td>
                  <td style={{ padding: '0.6rem' }}>{Number(r.total_qty || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.6rem' }}>{r.uom || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{Number(r.subtotal || 0).toFixed(2)}</td>
                  <td style={{ padding: '0.6rem' }}>{Number(r.tax_amount || 0).toFixed(2)}</td>
                  <td style={{ padding: '0.6rem', fontWeight: 'bold' }}>{Number(r.total_amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default FoodPurchaseOrder;
